// --- IMPORTS -------------------------------
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import '../report.css';
import ApiService from '../../../../services/apiService';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
// --- HELPERS -------------------------------
const numberFmt = (n, digits = 2) =>
  new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number.isFinite(+n) ? +n : 0);

// For <input type="date" />
const toDateInputValue = (d) => {
  const dt = new Date(d);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const COLUMNS = [
  { key: 'PeriodDisplay', label: 'Date', width: 160 },
  { key: 'TypeName', label: 'Type', width: 120 },
  { key: 'Quantity', label: 'QTY', align: 'right' },
  { key: 'NumbersOfOrders', label: 'Number Of Orders', align: 'right' },
  { key: 'Revenue', label: 'Total Sale Amount', align: 'right' },
];

// --- COMPONENT -------------------------------
const SaleSummaryReport = ({ Currency }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalRecords: 0,
    totalPages: 1,
    hasPrevious: false,
    hasNext: false,
  });

  // Default last 10 days
  const now = new Date();
  const tenDaysAgo = new Date(now);
  tenDaysAgo.setDate(now.getDate() - 10);

  const [formData, setFormData] = useState({
    fromDate: toDateInputValue(tenDaysAgo),
    toDate: toDateInputValue(now),
  });

  // ------------------------------------------------------------
  // Fetch Branches
  // ------------------------------------------------------------
  const fetchBranches = async () => {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_BRANCH');

      const list = res?.data?.result || [];
      const mapped = list.map((b) => ({
        value: b.id,
        label: b.branchName,
      }));

      setBranches(mapped);
    } catch (e) {
      console.error('Failed to fetch branches', e);
    }
  };

  // ------------------------------------------------------------
  // Fetch Report Data + Pagination
  // ------------------------------------------------------------
  const fetchData = async (page = 1, size = pagination.pageSize) => {
    setLoading(true);
    setErr('');

    try {
      const apiService = new ApiService();
      const params = new URLSearchParams();

      params.append('PageNo', page);
      params.append('PageSize', size);
      params.append('ReportType', 'Sales Summary');
      params.append('Measure', 'Revenue');
      params.append('StartDate', formData.fromDate);
      params.append('EndDate', formData.toDate);
      params.append('Comparison', 'No Comparison');

      // Always send BranchId → 0 = all branches
      params.append('BranchId', selectedBranch?.value || 0);

      const res = await apiService.getApiParamater('GET_SALE_SUMMERY', `?${params.toString()}`);

      const result = Array.isArray(res?.data?.result) ? res.data.result : [];
      const apiPg = res?.data?.paginationData || {};

      const normalized = result.map((r) => ({
        PeriodDisplay: r.PeriodDisplay || r.PeriodDate || '—',
        TypeName: r.TypeName || '—',
        Quantity: +r.Quantity || 0,
        NumbersOfOrders: +r.NumbersOfOrders || 0, // ✅ FIX
        Revenue: +r.Revenue || 0,
      }));

      setRows(normalized);

      const totalRecords = Number.isFinite(+apiPg.totalRecords) && +apiPg.totalRecords >= 0 ? +apiPg.totalRecords : normalized.length;

      const totalPages = Number.isFinite(+apiPg.totalPages) && +apiPg.totalPages > 0 ? +apiPg.totalPages : Math.max(1, Math.ceil(totalRecords / size));

      const hasPrevious = typeof apiPg.hasPrevious === 'boolean' ? apiPg.hasPrevious : page > 1;

      const hasNext = typeof apiPg.hasNext === 'boolean' ? apiPg.hasNext : page < totalPages;

      setPagination((p) => ({
        ...p,
        currentPage: page,
        pageSize: size,
        totalRecords,
        totalPages,
        hasPrevious,
        hasNext,
      }));
    } catch (e) {
      console.error(e);
      setErr('Failed to fetch report. Please try again.');
      setRows([]);
      setPagination((p) => ({
        ...p,
        totalRecords: 0,
        totalPages: 1,
        hasPrevious: false,
        hasNext: false,
      }));
    } finally {
      setLoading(false);
    }
  };

  // Load branches once
  useEffect(() => {
    fetchBranches();
  }, []);

  // ------------------------------------------------------------
  // Sorting Logic
  // ------------------------------------------------------------
  const [sortState, setSortState] = useState({ key: 'PeriodDisplay', dir: 'asc' });

  const sortedRows = useMemo(() => {
    const { key, dir } = sortState;
    const copy = [...rows];

    copy.sort((a, b) => {
      const av = a[key];
      const bv = b[key];

      if (Number.isFinite(+av) && Number.isFinite(+bv)) {
        return dir === 'asc' ? av - bv : bv - av;
      }

      const ad = new Date(av);
      const bd = new Date(bv);

      if (!isNaN(ad) && !isNaN(bd)) {
        return dir === 'asc' ? ad - bd : bd - ad;
      }

      return dir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });

    return copy;
  }, [rows, sortState]);

  // Totals
  const totals = useMemo(
    () => ({
      qty: sortedRows.reduce((s, r) => s + r.Quantity, 0),
      orders: sortedRows.reduce((s, r) => s + r.NumbersOfOrders, 0), // ✅ FIX
      rev: sortedRows.reduce((s, r) => s + r.Revenue, 0),
    }),
    [sortedRows],
  );

  // Handlers
  const onChangeRange = (e) => setFormData((old) => ({ ...old, [e.target.name]: e.target.value }));

  const onSearch = () => fetchData(1, pagination.pageSize);

  const onSort = (key) => setSortState((prev) => (prev.key !== key ? { key, dir: 'asc' } : { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }));

  const onPrevPage = () => {
    if (loading || !pagination.hasPrevious) return;
    fetchData(pagination.currentPage - 1, pagination.pageSize);
  };

  const onNextPage = () => {
    if (loading || !pagination.hasNext) return;
    fetchData(pagination.currentPage + 1, pagination.pageSize);
  };

  const onPageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10) || 20;
    fetchData(1, newSize);
  };

  // CSV Export
  const exportCSV = () => {
    const headers = COLUMNS.map((c) => `"${c.label}"`).join(',');
    const body = sortedRows.map((r) => COLUMNS.map((c) => `"${String(r[c.key] ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');

    const csv = `${headers}\n${body}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `sales-summary-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };
  const exportExcel = () => {
    if (!sortedRows.length) return;

    // Header
    const header = COLUMNS.map((c) => c.label);

    // Body (keep numbers as numbers for Excel)
    const body = sortedRows.map((r) =>
      COLUMNS.map((c) => {
        const v = r?.[c.key];

        if (c.key === 'Quantity') return Number(v) || 0;
        if (c.key === 'NumbersOfOrders') return Number(v) || 0;
        if (c.key === 'Revenue') return Number(v) || 0;

        return String(v ?? '').trim();
      }),
    );

    // Totals row (like footer)
    const totalsRow = [
      'Total',
      '', // Type column empty
      Number(totals.qty) || 0,
      Number(totals.orders) || 0,
      Number(totals.rev) || 0,
    ];

    const aoa = [header, ...body, totalsRow];

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // Optional widths
    ws['!cols'] = [
      { wch: 18 }, // Date
      { wch: 14 }, // Type
      { wch: 10 }, // QTY
      { wch: 18 }, // Number Of Orders
      { wch: 18 }, // Total Sale Amount
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sales Summary');

    const branchName = selectedBranch?.label ? selectedBranch.label.replace(/[\\/:*?"<>|]/g, '-') : 'All-Branches';
    const date = new Date().toISOString().slice(0, 10);

    XLSX.writeFile(wb, `sales-summary_${branchName}_${formData.fromDate}_to_${formData.toDate}_${date}.xlsx`);
  };
  const showingFrom = pagination.totalRecords === 0 ? 0 : (pagination.currentPage - 1) * pagination.pageSize + 1;
  const showingTo = pagination.totalRecords === 0 ? 0 : Math.min(pagination.currentPage * pagination.pageSize, pagination.totalRecords);

  return (
    <div className="p-5">
      <button className="addItem-btn mb-3" onClick={() => navigate(-1)}>
        ← Back
      </button>

      {/* Filters */}
      <div className="row g-3 align-items-end">
        <div className="col-md-3">
          <label className="form-label">Select Branch</label>
          <Select
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 999999 }),
              menu: (base) => ({ ...base, zIndex: 999999 }),
            }}
            value={selectedBranch}
            onChange={(opt) => setSelectedBranch(opt)}
            options={branches}
            placeholder="Select Branch"
            isClearable
          />
        </div>

        <div className="col-md-3">
          <label className="form-label">From Date</label>
          <input type="date" className="form-control" name="fromDate" value={formData.fromDate} onChange={onChangeRange} />
        </div>

        <div className="col-md-3">
          <label className="form-label">To Date</label>
          <input type="date" className="form-control" name="toDate" value={formData.toDate} onChange={onChangeRange} />
        </div>

        <div className="col-md-3 d-flex gap-3 align-items-end">
          <button className="addItem-btn" onClick={onSearch} disabled={loading}>
            {loading ? 'Loading…' : 'Search'}
          </button>

          <button className="btn btn-outline-secondary" onClick={exportCSV} disabled={!sortedRows.length}>
            Export CSV
          </button>

          <button className="btn btn-outline-success" onClick={exportExcel} disabled={!sortedRows.length}>
            Export Excel
          </button>
        </div>
      </div>

      {/* Pagination controls (top) */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mt-3 gap-2">
        <div className="text-muted">
          Showing <b>{showingFrom}</b>–<b>{showingTo}</b> of <b>{pagination.totalRecords}</b>
        </div>

        <div className="d-flex align-items-center gap-2">
          <label className="text-muted m-0">Page size</label>
          <select className="form-select form-select-sm" style={{ width: 90 }} value={pagination.pageSize} onChange={onPageSizeChange}>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>

          <button className="btn btn-sm btn-outline-secondary" onClick={onPrevPage} disabled={loading || !pagination.hasPrevious}>
            Prev
          </button>

          <span className="text-muted">
            Page <b>{pagination.currentPage}</b> / <b>{pagination.totalPages}</b>
          </span>

          <button className="btn btn-sm btn-outline-secondary" onClick={onNextPage} disabled={loading || !pagination.hasNext}>
            Next
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="POsgridshad rounded p-0 mt-3">
        {err && <div className="alert alert-danger m-3">{err}</div>}

        <div className="custom-table-container">
          <table className="custom-table">
            <thead>
              <tr>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => onSort(c.key)}
                    className={c.align === 'right' ? 'text-end' : ''}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    {c.label}
                    {sortState.key === c.key ? (sortState.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {!loading && sortedRows.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center p-4">
                    No data found
                  </td>
                </tr>
              )}

              {!loading &&
                sortedRows.map((r, index) => (
                  <tr key={index}>
                    <td>{r.PeriodDisplay}</td>
                    <td>{r.TypeName}</td>
                    <td className="text-end">{r.Quantity}</td>
                    <td className="text-end">{r.NumbersOfOrders}</td> {/* ✅ FIX */}
                    <td className="text-end">
                      {Currency?.currency?.code} {numberFmt(r.Revenue)}
                    </td>
                  </tr>
                ))}

              {loading && (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center p-4">
                    Loading…
                  </td>
                </tr>
              )}
            </tbody>

            {sortedRows.length > 0 && (
              <tfoot>
                <tr className="fw-bold bg-light">
                  <td colSpan={2}>Total</td>
                  <td className="text-end">{totals.qty}</td>
                  <td className="text-end">{totals.orders}</td> {/* ✅ FIX */}
                  <td className="text-end">
                    {Currency?.currency?.code} {numberFmt(totals.rev)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Pagination controls (bottom) */}
        <div className="d-flex justify-content-end align-items-center gap-2 p-3">
          <button className="btn btn-sm btn-outline-secondary" onClick={onPrevPage} disabled={loading || !pagination.hasPrevious}>
            Prev
          </button>
          <span className="text-muted">
            Page <b>{pagination.currentPage}</b> / <b>{pagination.totalPages}</b>
          </span>
          <button className="btn btn-sm btn-outline-secondary" onClick={onNextPage} disabled={loading || !pagination.hasNext}>
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

// Redux
const mapStateToProps = (state) => state;
export default connect(mapStateToProps)(SaleSummaryReport);
